import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Badge, 
  Spinner,
  Row,
  Col,
  Card,
  InputGroup,
  Pagination
} from 'react-bootstrap';
import axios from 'axios';

const UserEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Make this changeable
  const [totalItems, setTotalItems] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phonenumber: '',
    location: '',
    budget: '',
    dates: '',
    person: '',
    chatid: '',
    status: 'interested',
    caller_id: '',
    brand_name: '',
    call_id: '',
    scoring: null,
    enquiry_type: '',
    summary: '',
    viewing_date: '',
    apartment: null,
    link: '',
    flgstatus: ''
  });

  // Calculate pagination
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEnquiries = enquiries.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleFirstPage = () => {
    setCurrentPage(1);
  };

  const handleLastPage = () => {
    setCurrentPage(totalPages);
  };

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Update total items when enquiries change
  useEffect(() => {
    setTotalItems(enquiries.length);
  }, [enquiries]);

  // Reset to first page when enquiries change
  useEffect(() => {
    setCurrentPage(1);
  }, [enquiries]);

  // Input validation functions
  const validatePhoneNumber = (value) => {
    // Allow only numbers, +, -, (, ), and spaces
    return value.replace(/[^0-9+\-() ]/g, '');
  };

  const validateNumeric = (value) => {
    // Allow only numbers and decimal point
    return value.replace(/[^0-9.]/g, '');
  };

  const validateInteger = (value) => {
    // Allow only numbers
    return value.replace(/[^0-9]/g, '');
  };

  const validateDate = (value) => {
    // Allow only numbers, -, /, and spaces for date formats
    return value.replace(/[^0-9\-\/ ]/g, '');
  };

  const validateBudget = (value) => {
    // Allow numbers, currency symbols, commas, and hyphens for ranges
    return value.replace(/[^0-9£$€,.\-\s]/g, '');
  };

  // Input handlers with validation
  const handlePhoneChange = (e) => {
    const validatedValue = validatePhoneNumber(e.target.value);
    setFormData({...formData, phonenumber: validatedValue});
  };

  const handleSearchPhoneChange = (e) => {
    const validatedValue = validatePhoneNumber(e.target.value);
    setSearchPhone(validatedValue);
  };

  const handleBudgetChange = (e) => {
    const validatedValue = validateBudget(e.target.value);
    setFormData({...formData, budget: validatedValue});
  };

  const handleDatesChange = (e) => {
    const validatedValue = validateDate(e.target.value);
    setFormData({...formData, dates: validatedValue});
  };

  const handlePersonChange = (e) => {
    const validatedValue = validateInteger(e.target.value);
    setFormData({...formData, person: validatedValue});
  };

  const handleScoringChange = (e) => {
    const validatedValue = validateInteger(e.target.value);
    setFormData({...formData, scoring: validatedValue ? parseInt(validatedValue) : null});
  };

  const handleChatIdChange = (e) => {
    const validatedValue = validateNumeric(e.target.value);
    setFormData({...formData, chatid: validatedValue});
  };

  const handleCallerIdChange = (e) => {
    const validatedValue = validateNumeric(e.target.value);
    setFormData({...formData, caller_id: validatedValue});
  };

  const handleCallIdChange = (e) => {
    const validatedValue = validateNumeric(e.target.value);
    setFormData({...formData, call_id: validatedValue});
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/user-enquires/');
      setEnquiries(response.data);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      let errorMessage = 'Failed to fetch enquiries';
      
      if (error.response?.data?.detail) {
        errorMessage += ': ' + error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage += ': ' + error.response.data.message;
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      } else {
        errorMessage += ': Unknown error occurred';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // Clean up the form data before sending
      const cleanFormData = {
        ...formData,
        scoring: formData.scoring ? parseInt(formData.scoring) : null,
        viewing_date: formData.viewing_date && formData.viewing_date !== '' ? formData.viewing_date : null,
        apartment: formData.apartment && typeof formData.apartment === 'object' ? formData.apartment : null
      };
      
      // Remove empty strings and convert to null
      Object.keys(cleanFormData).forEach(key => {
        if (cleanFormData[key] === '') {
          cleanFormData[key] = null;
        }
      });
      
      console.log('Sending form data:', cleanFormData);
      console.log('Form data JSON:', JSON.stringify(cleanFormData));
      
      if (editingEnquiry) {
        await axios.put(`/api/user-enquires/${editingEnquiry.id}`, cleanFormData);
        setSuccess('Enquiry updated successfully');
      } else {
        await axios.post('/api/user-enquires/', cleanFormData);
        setSuccess('Enquiry created successfully');
      }
      
      fetchEnquiries();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving enquiry:', error);
      console.error('Error type:', typeof error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to save enquiry';
      
      if (error.response?.data?.detail) {
        errorMessage += ': ' + error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage += ': ' + error.response.data.message;
      } else if (error.response?.statusText) {
        errorMessage += ': ' + error.response.statusText;
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      } else if (typeof error === 'string') {
        errorMessage += ': ' + error;
      } else {
        errorMessage += ': Unknown error occurred';
      }
      
      setError(errorMessage);
    }
  };

  const handleDelete = async (enquiryId) => {
    if (window.confirm('Are you sure you want to delete this enquiry?')) {
      try {
        await axios.delete(`/api/user-enquires/${enquiryId}`);
        setEnquiries(enquiries.filter(enquiry => enquiry.id !== enquiryId));
        setSuccess('Enquiry deleted successfully');
      } catch (error) {
        console.error('Error deleting enquiry:', error);
        let errorMessage = 'Failed to delete enquiry';
        
        if (error.response?.data?.detail) {
          errorMessage += ': ' + error.response.data.detail;
        } else if (error.response?.data?.message) {
          errorMessage += ': ' + error.response.data.message;
        } else if (error.message) {
          errorMessage += ': ' + error.message;
        } else {
          errorMessage += ': Unknown error occurred';
        }
        
        setError(errorMessage);
      }
    }
  };

  const handleEdit = (enquiry) => {
    setEditingEnquiry(enquiry);
    setFormData({
      name: enquiry.name || '',
      email: enquiry.email || '',
      phonenumber: enquiry.phonenumber || '',
      location: enquiry.location || '',
      budget: enquiry.budget || '',
      dates: enquiry.dates || '',
      person: enquiry.person || '',
      chatid: enquiry.chatid || '',
      status: enquiry.status || 'interested',
      caller_id: enquiry.caller_id || '',
      brand_name: enquiry.brand_name || '',
      call_id: enquiry.call_id || '',
      scoring: enquiry.scoring || null,
      enquiry_type: enquiry.enquiry_type || '',
      summary: enquiry.summary || '',
      viewing_date: enquiry.viewing_date || '',
      apartment: enquiry.apartment || null,
      link: enquiry.link || '',
      flgstatus: enquiry.flgstatus || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEnquiry(null);
    setFormData({
      name: '',
      email: '',
      phonenumber: '',
      location: '',
      budget: '',
      dates: '',
      person: '',
      chatid: '',
      status: 'interested',
      caller_id: '',
      brand_name: '',
      call_id: '',
      scoring: null,
      enquiry_type: '',
      summary: '',
      viewing_date: '',
      apartment: null,
      link: '',
      flgstatus: ''
    });
  };

  const handleSearch = async () => {
    if (!searchPhone.trim()) {
      setError('Please enter a phone number to search');
      return;
    }

    try {
      setSearchLoading(true);
      setError('');
      const response = await axios.get(`/api/user-enquires/search/${searchPhone}`);
      setSearchResult(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setSearchResult(null);
        setError('No enquiry found with this phone number');
      } else {
        setError('Failed to search: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'interested': 'success',
      'not_interested': 'danger',
      'spam': 'secondary',
      'book_a_viewing': 'primary',
      'cancelled': 'danger',
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <Row className="mb-4">
        <Col>
          <h2>User Enquiries Management</h2>
          <p className="text-muted">Manage user enquiries and search by phone number</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => setShowModal(true)}>
            + Add New Enquiry
          </Button>
        </Col>
      </Row>

      {/* Search Section */}
      <Card className="mb-4">
        <Card.Header>
          <h5>Search by Phone Number</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Enter phone number (e.g., +1234567890 or 1234567890)"
                  value={searchPhone}
                  onChange={handleSearchPhoneChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button 
                  variant="outline-secondary" 
                  onClick={handleSearch}
                  disabled={searchLoading}
                >
                  {searchLoading ? <Spinner animation="border" size="sm" /> : 'Search'}
                </Button>
              </InputGroup>
            </Col>
          </Row>
          
          {searchResult && (
            <Row className="mt-3">
              <Col>
                <Card className="border-success">
                  <Card.Header className="bg-success text-white">
                    Search Result Found
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <strong>Name:</strong> {searchResult.name || 'N/A'}<br />
                        <strong>Email:</strong> {searchResult.email || 'N/A'}<br />
                        <strong>Phone:</strong> {searchResult.phonenumber || 'N/A'}<br />
                        <strong>Location:</strong> {searchResult.location || 'N/A'}
                      </Col>
                      <Col md={6}>
                        <strong>Budget:</strong> {searchResult.budget || 'N/A'}<br />
                        <strong>Scoring:</strong> {searchResult.scoring || 'N/A'}<br />
                        <strong>Status:</strong> {getStatusBadge(searchResult.status)}<br />
                        <strong>Caller ID:</strong> {searchResult.caller_id || 'N/A'}<br />
                        <strong>Brand:</strong> {searchResult.brand_name || 'N/A'}<br />
                        <strong>Enquiry Type:</strong> {searchResult.enquiry_type || 'N/A'}<br />
                        <strong>Summary:</strong> {searchResult.summary || 'N/A'}<br />
                        <strong>Viewing Date:</strong> {searchResult.viewing_date ? new Date(searchResult.viewing_date).toLocaleString() : 'N/A'}<br />
                        <strong>Apartment:</strong> {searchResult.apartment ? JSON.stringify(searchResult.apartment) : 'N/A'}
                      </Col>
                    </Row>
                    <div className="mt-3">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => handleEdit(searchResult)}
                      >
                        Edit This Enquiry
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Enquiries Table */}
      <Card>
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h5>All Enquiries ({totalItems})</h5>
            </Col>
            <Col xs="auto">
              <Form.Select 
                value={itemsPerPage} 
                onChange={(e) => {
                  setItemsPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                size="sm"
                style={{ width: 'auto' }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Table responsive striped hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Location</th>
                <th>Budget</th>
                <th>Scoring</th>
                <th>Enquiry Type</th>
                <th>Summary</th>
                <th>Viewing Date</th>
                <th>Link</th>
                <th>FLG Status</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentEnquiries.map((enquiry) => (
                <tr key={enquiry.id}>
                  <td>{enquiry.id}</td>
                  <td>{enquiry.name || 'N/A'}</td>
                  <td>{enquiry.email || 'N/A'}</td>
                  <td>{enquiry.phonenumber || 'N/A'}</td>
                  <td>{enquiry.location || 'N/A'}</td>
                  <td>{enquiry.budget || 'N/A'}</td>
                  <td>{enquiry.scoring || 'N/A'}</td>
                  <td>{enquiry.enquiry_type || 'N/A'}</td>
                  <td>{enquiry.summary ? (enquiry.summary.length > 30 ? enquiry.summary.substring(0, 30) + '...' : enquiry.summary) : 'N/A'}</td>
                  <td>{enquiry.viewing_date ? new Date(enquiry.viewing_date).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    {enquiry.link ? (
                      <a href={enquiry.link} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                        Link
                      </a>
                    ) : 'N/A'}
                  </td>
                  <td>
                    <Badge bg={enquiry.flgstatus === 'success' ? 'success' : enquiry.flgstatus === 'failed' ? 'danger' : 'secondary'}>
                      {enquiry.flgstatus || 'N/A'}
                    </Badge>
                  </td>
                  <td>{getStatusBadge(enquiry.status)}</td>
                  <td>{formatDate(enquiry.created_at)}</td>
                  <td>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      className="me-2"
                      onClick={() => handleEdit(enquiry)}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => handleDelete(enquiry.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Card className="mt-3">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={6}>
                <p className="mb-0 text-muted">
                  Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} enquiries
                </p>
              </Col>
              <Col md={6}>
                <div className="d-flex justify-content-end">
                  <Pagination className="mb-0">
                    <Pagination.First 
                      onClick={handleFirstPage} 
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev 
                      onClick={handlePreviousPage} 
                      disabled={currentPage === 1}
                    />
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }
                      
                      return (
                        <Pagination.Item
                          key={pageNumber}
                          active={pageNumber === currentPage}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Pagination.Item>
                      );
                    })}
                    
                    <Pagination.Next 
                      onClick={handleNextPage} 
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last 
                      onClick={handleLastPage} 
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Modal for Create/Edit */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingEnquiry ? 'Edit Enquiry' : 'Add New Enquiry'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.phonenumber}
                    onChange={handlePhoneChange}
                    placeholder="e.g., +1234567890 or 1234567890"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Budget</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.budget}
                    onChange={handleBudgetChange}
                    placeholder="e.g., £1000-2000 or $1500"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Dates</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.dates}
                    onChange={handleDatesChange}
                    placeholder="e.g., 2024-01-15 or 15/01/2024"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of People</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.person}
                    onChange={handlePersonChange}
                    placeholder="Enter number only"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="interested">Interested</option>
                    <option value="not_interested">Not Interested</option>
                    <option value="spam">Spam</option>
                    <option value="book_a_viewing">Book a Viewing</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Chat ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.chatid}
                    onChange={handleChatIdChange}
                    placeholder="Enter numeric ID"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Caller ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.caller_id}
                    onChange={handleCallerIdChange}
                    placeholder="Enter numeric ID"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Brand Name</Form.Label>
                  <Form.Select
                    value={formData.brand_name}
                    onChange={(e) => setFormData({...formData, brand_name: e.target.value})}
                  >
                    <option value="">Select Brand</option>
                    <option value="Parklane">Parklane</option>
                    <option value="Rentinc">Rentinc</option>
                    <option value="Iconinc">Iconinc</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Call ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.call_id}
                    onChange={handleCallIdChange}
                    placeholder="Enter numeric ID"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Scoring</Form.Label>
                  <Form.Control
                    type="text"
                    name="scoring"
                    value={formData.scoring || ''}
                    onChange={handleScoringChange}
                    placeholder="Enter score (1-100)"
                    min="1"
                    max="100"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Brand</Form.Label>
                  <Form.Select
                    name="brand"
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                  >
                    <option value="">Select Brand</option>
                    <option value="Parklane">Parklane</option>
                    <option value="Rentinc">Rentinc</option>
                    <option value="Iconinc">Iconinc</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Summary</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.summary}
                    onChange={(e) => setFormData({...formData, summary: e.target.value})}
                    placeholder="Enter summary of the enquiry"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Link</Form.Label>
                  <Form.Control
                    type="url"
                    value={formData.link}
                    onChange={(e) => setFormData({...formData, link: e.target.value})}
                    placeholder="Enter link URL"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>FLG Status</Form.Label>
                  <Form.Select
                    value={formData.flgstatus}
                    onChange={(e) => setFormData({...formData, flgstatus: e.target.value})}
                  >
                    <option value="">Select FLG Status</option>
                    <option value="pending">Pending</option>
                    <option value="sent">Sent</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                    <option value="error">Error</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Viewing Date</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={formData.viewing_date ? new Date(formData.viewing_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setFormData({...formData, viewing_date: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Apartment (JSON)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.apartment ? JSON.stringify(formData.apartment, null, 2) : ''}
                    onChange={(e) => {
                      try {
                        const parsed = e.target.value ? JSON.parse(e.target.value) : null;
                        setFormData({...formData, apartment: parsed});
                      } catch (error) {
                        // Keep the raw text if it's not valid JSON
                        setFormData({...formData, apartment: e.target.value});
                      }
                    }}
                    placeholder='{"property_id": "123", "address": "123 Main St", "details": "..."} or leave empty'
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingEnquiry ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default UserEnquiries; 